import { useStableFn } from '@/hooks/use-stable-fn';
import {
  buyInstantTicketAtom,
  BuyInstantTicketPayload,
  buyTicketAtom,
  BuyTicketPayload,
  programAtom,
} from '@/stores/program';
import { Ufc, UfcEvent, UfcEventData } from '@/utils/program/idl/type';
import { Program } from '@coral-xyz/anchor';
import { Trans } from '@lingui/react/macro';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { unstable_serialize, useSWRConfig } from 'swr';
import { useSnackbar } from './use-snackbar';

export const useProgramEvent = <E extends UfcEvent>(
  event: E & string,
  fn: (event: {
    data: UfcEventData[E];
    slot: number;
    signature: string;
    program: Program<Ufc>;
  }) => any
) => {
  const program = useAtomValue(programAtom);
  const handler = useStableFn(fn);

  useEffect(() => {
    if (!program) return;

    const id = program.addEventListener(event, (data, slot, signature) => {
      handler({ data, signature, program, slot });
    });

    return () => {
      program.removeEventListener(id);
    };
  }, [program, event]);
};

export const useBuyTicket = () => {
  const [loading, setLoading] = useState(false);
  const { mutate } = useSWRConfig();
  const { enqueueSnackbar } = useSnackbar();

  const buyTicket = useAtomCallback(
    useCallback(async (get, set, data: BuyTicketPayload) => {
      try {
        setLoading(true);
        const tx = await set(buyTicketAtom, data);
        mutate((key) => unstable_serialize(key).includes('getGames'));
        enqueueSnackbar(<Trans>Bet placed. Good luck!</Trans>, {
          variant: 'success',
        });

        return tx;
      } catch (err: any) {
        enqueueSnackbar(
          err?.transactionMessage ?? (
            <Trans>Something went wrong, please try again</Trans>
          ),
          {
            variant: 'error',
          }
        );
      } finally {
        setLoading(false);
      }
    }, [])
  );

  return { loading, buyTicket };
};

export const useInstantBuyTicket = () => {
  const [loading, setLoading] = useState(false);
  const { mutate } = useSWRConfig();
  const { enqueueSnackbar } = useSnackbar();

  const buyTicket = useAtomCallback(
    useCallback(async (get, set, data: BuyInstantTicketPayload) => {
      try {
        setLoading(true);

        const program = get(programAtom);
        if (!program || !program.provider.publicKey) return;

        const balance = await program.provider.connection.getBalance(
          program.provider.publicKey
        );

        const totalStakes = _.reduce(
          data.stakes,
          (acc, stake) => acc + Number(stake),
          0
        );

        if (balance < totalStakes) {
          return enqueueSnackbar(<Trans>Insufficient balance</Trans>, {
            variant: 'error',
          });
        }

        const tx = await set(buyInstantTicketAtom, data);
        mutate((key) => unstable_serialize(key).includes('currentRound'));
        enqueueSnackbar(<Trans>Bet placed. Good luck!</Trans>, {
          variant: 'success',
        });

        return tx;
      } catch (err: any) {
        enqueueSnackbar(
          err?.error?.errorMessage ?? err?.error?.transactionMessage ?? (
            <Trans>Something went wrong, please try again</Trans>
          ),
          { variant: 'error' }
        );
      } finally {
        setLoading(false);
      }
    }, [])
  );

  return { loading, buyTicket };
};
